import { useState, type FormEvent, type ReactElement } from 'react';
import type { OptInMethod, Uuid } from '@zenda/api-contract';
import { Modal } from './Modal';
import { useT } from '../lib/i18n';
import { useCreatePatient, usePatients } from '../lib/queries';
import { erroDoTelefone, mascararTelefone, telefoneCanonico, telefoneE164 } from '../lib/mascaras';
import '../styles/clients.css';

export const METODOS_DE_OPT_IN: readonly OptInMethod[] = [
  'presencial',
  'telefone',
  'papel',
  'outro',
];

/**
 * Cadastro manual de paciente (ADR 0028).
 *
 * O número já cadastrado neste cliente não é erro: a ficha que existe abre. O servidor
 * nunca diz se o número é paciente de outro médico, e a tela também não pergunta.
 */
export function NovoPaciente({
  clientId,
  onClose,
  onCriado,
}: {
  readonly clientId: Uuid;
  readonly onClose: () => void;
  readonly onCriado: (contactId: Uuid, jaEra: boolean) => void;
}): ReactElement {
  const t = useT();
  const criar = useCreatePatient(clientId);
  const [nome, setNome] = useState('');
  const [telefone, setTelefone] = useState('');
  const [autorizou, setAutorizou] = useState(false);
  const [metodo, setMetodo] = useState<OptInMethod>('presencial');
  const [invalido, setInvalido] = useState<string | null>(null);
  const [erroDoFone, setErroDoFone] = useState<string | null>(null);
  const canonico = telefoneCanonico(telefone);
  const e164 = telefoneE164(telefone);
  const mesmoNumero = usePatients(clientId, e164 ?? '', false, e164 !== null);
  const jaCadastrado = e164 === null ? undefined : mesmoNumero.data?.find((p) => p.phone === e164);

  function enviar(e: FormEvent): void {
    e.preventDefault();
    if (nome.trim().length < 2) return setInvalido(t.patients.novo.nameRequired);
    if (telefone.trim() === '') return setInvalido(t.patients.novo.phoneRequired);
    const problema = erroDoTelefone(telefone);
    if (problema !== null) {
      setErroDoFone(problema);
      return setInvalido(null);
    }
    setInvalido(null);
    criar.mutate(
      { name: nome.trim(), phone: telefone, optInMethod: autorizou ? metodo : null },
      { onSuccess: (r) => onCriado(r.contactId, r.alreadyLinked) },
    );
  }

  const erro = invalido ?? (criar.isError ? criar.error.message : null);

  return (
    <Modal
      title={t.patients.novo.title}
      subtitle={t.patients.novo.subtitle}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            {t.common.cancel}
          </button>
          <button
            type="submit"
            form="novo-paciente"
            className="btn btn--primary"
            disabled={criar.isPending}
          >
            {criar.isPending ? t.common.saving : t.patients.novo.submit}
          </button>
        </>
      }
    >
      <form id="novo-paciente" className="form" onSubmit={enviar}>
        {erro === null ? null : <p className="form__error">{erro}</p>}

        <div className="field">
          <label className="label" htmlFor="np-nome">
            {t.patients.novo.name}
          </label>
          <input
            id="np-nome"
            className="input"
            value={nome}
            maxLength={120}
            onChange={(e) => setNome(e.target.value)}
            placeholder={t.patients.novo.namePlaceholder}
            autoComplete="off"
            autoFocus
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="np-fone">
            {t.patients.novo.phone}
          </label>
          <input
            id="np-fone"
            className={`input num${erroDoFone === null ? '' : ' is-invalid'}`}
            type="tel"
            inputMode="tel"
            value={telefone}
            onChange={(e) => {
              setTelefone(mascararTelefone(e.target.value));
              setErroDoFone(null);
            }}
            onBlur={() => setErroDoFone(telefone.trim() === '' ? null : erroDoTelefone(telefone))}
            placeholder={t.patients.novo.phonePlaceholder}
            aria-invalid={erroDoFone !== null}
            autoComplete="off"
          />
          {erroDoFone !== null ? (
            <span className="form__error">{erroDoFone}</span>
          ) : jaCadastrado !== undefined ? (
            <span className="np-ja">{t.patients.novo.jaCadastrado(jaCadastrado.name)}</span>
          ) : canonico === null ? (
            <span className="form__hint">{t.patients.novo.phoneHint}</span>
          ) : (
            <span className="form__hint">{t.patients.novo.phoneOk(canonico)}</span>
          )}
        </div>

        <label className="np-confirma">
          <input
            type="checkbox"
            className="cbx"
            checked={autorizou}
            onChange={() => setAutorizou((v) => !v)}
          />
          <span>{t.patients.optIn.declare}</span>
        </label>
        {autorizou ? (
          <div className="field">
            <label className="label" htmlFor="np-metodo">
              {t.patients.optIn.method}
            </label>
            <select
              id="np-metodo"
              className="select"
              value={metodo}
              onChange={(e) => setMetodo(e.target.value as OptInMethod)}
            >
              {METODOS_DE_OPT_IN.map((m) => (
                <option key={m} value={m}>
                  {t.patients.optIn.methods[m]}
                </option>
              ))}
            </select>
          </div>
        ) : (
          <p className="np-hint">{t.patients.optIn.hint}</p>
        )}
      </form>
    </Modal>
  );
}
